import classnames from 'classnames';
import React, { ImgHTMLAttributes, useEffect, useState } from 'react';
import { useInViewOnce } from 'shared/hooks/useInViewOnce';

interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
	placeholderImg?: string;
	backgroundImg?: boolean;
	className?: string;
	inactiveClassName?: string;
	loadedClassName?: string;
	delay?: number;
	alt: string;
	src: string;
}

/**
 * Component that will render an <img> tag that will have its source image lazily loaded only once
 * the element is visible in the viewport.
 * @param props
 */
export default function LazyImage({
	placeholderImg = '/images/placeholder/placeholder-image.png',
	src,
	alt,
	backgroundImg = false,
	delay = 0,
	className,
	inactiveClassName = 'roc-lazy-image--inactive',
	loadedClassName = 'roc-lazy-image--loaded',
	...rest
}: LazyImageProps) {
	const [ref, viewed] = useInViewOnce({
		threshold: 0,
	});

	const [loadedClassDisplayed, setLoadedClassDisplayed] = useState(false);

	useEffect(() => {
		if (viewed) {
			setTimeout(() => {
				setLoadedClassDisplayed(true);
			}, delay);
		}
	}, [viewed, delay]);

	const imageSrc = viewed ? src : placeholderImg;

	const classes = classnames(className, {
		[inactiveClassName]: !viewed,
		[loadedClassName]: loadedClassDisplayed,
	});

	if (backgroundImg) {
		return <div ref={ref} style={{ backgroundImage: 'url(' + imageSrc + ')' }} className={classes} {...rest} />;
	} else {
		return <img ref={ref} src={imageSrc} className={classes} alt={alt} {...rest} />;
	}
}
